/**
 * Custom Price Range Slider Component
 * A modern, accessible dual-handle range slider for price filtering
 */
class PriceRangeSlider extends HTMLElement {
  constructor() {
    super();
    this.min = 0;
    this.max = 100;
    this.step = 1;
    this.minValue = 0;
    this.maxValue = 100;
    this.isDragging = false;
    this.activeHandle = null;
    this.startX = 0;
    this.startValue = 0;
    this.storageKey = null;
    this.isInitialized = false;
    this.windowEventsBound = false;

    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleFiltersClear = this.handleFiltersClear.bind(this);
  }

  static get observedAttributes() {
    return ['min', 'max', 'step', 'min-value', 'max-value'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === 'min') {
      this.min = parseFloat(newValue) || 0;
    } else if (name === 'max') {
      this.max = parseFloat(newValue) || 100;
    } else if (name === 'step') {
      this.step = parseFloat(newValue) || 1;
    } else if (name === 'min-value') {
      const val = parseFloat(newValue);
      if (!isNaN(val)) this.minValue = val;
    } else if (name === 'max-value') {
      const val = parseFloat(newValue);
      if (!isNaN(val)) this.maxValue = val;
    }

    if (this.isConnected && this.isInitialized && !this.isDragging) {
      this.ensureRendered();
      this.normalizeStep();
      this.validateValues();
      this.updateSliderPosition();
    }
  }

  connectedCallback() {
    // Generate unique storage key based on filter name
    const container = this.closest('[data-range-holder]');
    if (container) {
      const filterName = container.id || 'price-filter';
      this.storageKey = `price-slider-${filterName}`;
    }

    // Protect against DOM updates wiping content (morphdom)
    this.observer = new MutationObserver((mutations) => {
      if (!this.querySelector('.price-range-slider')) {
        this.render();
        this.setupEventListeners();
        this.updateSliderPosition();
      }
    });

    this.observer.observe(this, { childList: true });

    // Use requestAnimationFrame to ensure children and siblings are ready
    requestAnimationFrame(() => {
      this.initializeValues();
      this.render();
      this.setupEventListeners();
      this.syncFromInputsOrAttributes();
      this.updateSliderPosition();
      this.isInitialized = true;
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.removeEventListeners();
  }

  initializeValues() {
    this.min = parseFloat(this.getAttribute('min')) || 0;
    this.max = parseFloat(this.getAttribute('max')) || 100;
    this.step = parseFloat(this.getAttribute('step')) || 1;
    this.normalizeStep();

    this.minValue = this.min;
    this.maxValue = this.max;

    const attrMinValue = parseFloat(this.getAttribute('min-value'));
    const attrMaxValue = parseFloat(this.getAttribute('max-value'));
    const hasAttrMin = !isNaN(attrMinValue);
    const hasAttrMax = !isNaN(attrMaxValue);

    if (hasAttrMin && attrMinValue >= this.min && attrMinValue <= this.max) {
      this.minValue = attrMinValue;
    }
    if (hasAttrMax && attrMaxValue >= this.min && attrMaxValue <= this.max) {
      this.maxValue = attrMaxValue;
    }

    if (!hasAttrMin && !hasAttrMax) {
      this.restoreFromStorage();
    }
    this.validateValues();
  }

  validateValues() {
    if (!Number.isFinite(this.minValue)) this.minValue = this.min;
    if (!Number.isFinite(this.maxValue)) this.maxValue = this.max;

    this.minValue = Math.max(this.min, Math.min(this.max, this.minValue));
    this.maxValue = Math.max(this.min, Math.min(this.max, this.maxValue));

    if (this.minValue > this.maxValue) {
      this.minValue = this.maxValue;
    }
  }

  render() {
    if (this.querySelector('.price-range-slider__track') && this.querySelector('.price-range-slider__handle')) {
      this.cacheElements();
      return;
    }

    this.innerHTML = `
      <div class="price-range-slider">
        <div class="price-range-slider__track">
          <div class="price-range-slider__range"></div>
        </div>
        <div class="price-range-slider__handle price-range-slider__handle--min"
             tabindex="0"
             role="slider"
             aria-label="Minimum price"
             aria-valuemin="${this.min}"
             aria-valuemax="${this.max}"
             aria-valuenow="${this.minValue}">
        </div>
        <div class="price-range-slider__handle price-range-slider__handle--max"
             tabindex="0"
             role="slider"
             aria-label="Maximum price"
             aria-valuemin="${this.min}"
             aria-valuemax="${this.max}"
             aria-valuenow="${this.maxValue}">
        </div>
      </div>
    `;

    this.cacheElements();
  }

  cacheElements() {
    this.track = this.querySelector('.price-range-slider__track');
    this.range = this.querySelector('.price-range-slider__range');
    this.minHandle = this.querySelector('.price-range-slider__handle--min');
    this.maxHandle = this.querySelector('.price-range-slider__handle--max');
  }

  setupEventListeners() {
    if (!this.minHandle || !this.maxHandle) return;

    // Mouse events
    this.minHandle.addEventListener('mousedown', (e) => this.handleMouseDown(e, 'min'));
    this.maxHandle.addEventListener('mousedown', (e) => this.handleMouseDown(e, 'max'));

    // Touch events
    this.minHandle.addEventListener('touchstart', (e) => this.handleTouchStart(e, 'min'), { passive: false });
    this.maxHandle.addEventListener('touchstart', (e) => this.handleTouchStart(e, 'max'), { passive: false });

    // Keyboard events
    this.minHandle.addEventListener('keydown', (e) => this.handleKeyDown(e, 'min'));
    this.maxHandle.addEventListener('keydown', (e) => this.handleKeyDown(e, 'max'));

    if (!this.windowEventsBound) {
      // Window events
      window.addEventListener('resize', this.handleResize);

      document.addEventListener('theme:filters:clear', this.handleFiltersClear);
      this.windowEventsBound = true;
    }

    this.syncWithInputs();
  }

  removeEventListeners() {
    if (this.windowEventsBound) {
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('theme:filters:clear', this.handleFiltersClear);
      this.windowEventsBound = false;
    }
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
  }

  updateSliderPosition() {
    if (!this.track || !this.range || !this.minHandle || !this.maxHandle) return;

    const range = this.max - this.min;
    if (!Number.isFinite(range) || range <= 0) {
      this.range.style.left = '0%';
      this.range.style.width = '0%';
      this.minHandle.style.left = '0%';
      this.maxHandle.style.left = '0%';
      this.updateInputValues();
      this.updateLabels();
      return;
    }

    const minPercent = ((this.minValue - this.min) / range) * 100;
    const maxPercent = ((this.maxValue - this.min) / range) * 100;
    const clampedMinPercent = Math.max(0, Math.min(100, minPercent));
    const clampedMaxPercent = Math.max(0, Math.min(100, maxPercent));

    // Update range bar
    this.range.style.left = `${clampedMinPercent}%`;
    this.range.style.width = `${clampedMaxPercent - clampedMinPercent}%`;

    // Update handles
    this.minHandle.style.left = `${clampedMinPercent}%`;
    this.maxHandle.style.left = `${clampedMaxPercent}%`;

    // Update ARIA attributes
    this.minHandle.setAttribute('aria-valuenow', this.minValue);
    this.maxHandle.setAttribute('aria-valuenow', this.maxValue);

    // Update input values and labels
    this.updateInputValues();
    this.updateLabels();
  }

  handleMouseDown(e, handle) {
    e.preventDefault();
    this.startDrag(e.clientX, handle);
  }

  handleTouchStart(e, handle) {
    e.preventDefault();
    const touch = e.touches[0];
    this.startDrag(touch.clientX, handle);
  }

  startDrag(clientX, handle) {
    this.isDragging = true;
    this.activeHandle = handle;
    this.startX = clientX;
    this.startValue = handle === 'min' ? this.minValue : this.maxValue;

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);

    this.classList.add('is-dragging');
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    this.updateValue(e.clientX);
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    this.updateValue(touch.clientX);
  }

  handleMouseUp() {
    this.endDrag();
  }

  handleTouchEnd() {
    this.endDrag();
  }

  endDrag() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.activeHandle = null;

    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);

    this.classList.remove('is-dragging');

    this.saveToStorage();
    this.emitRangeUpdate();
  }

  updateValue(clientX) {
    const trackRect = this.track.getBoundingClientRect();
    if (!trackRect.width) return;
    const deltaX = clientX - this.startX;
    const deltaPercent = (deltaX / trackRect.width) * 100;
    const deltaValue = deltaPercent * (this.max - this.min) / 100;

    let newValue = this.startValue + deltaValue;

    // Apply step constraint
    if (this.step > 0) {
      newValue = Math.round(newValue / this.step) * this.step;
    }

    // Constrain to bounds
    newValue = Math.max(this.min, Math.min(this.max, newValue));

    // Prevent handles from crossing
    if (this.activeHandle === 'min') {
      newValue = Math.min(newValue, this.maxValue - this.step);
      this.minValue = newValue;
    } else {
      newValue = Math.max(newValue, this.minValue + this.step);
      this.maxValue = newValue;
    }

    this.updateSliderPosition();
  }

  handleKeyDown(e, handle) {
    const step = this.step || 1;
    let newValue;
    let shouldUpdate = false;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        if (handle === 'min') {
          newValue = Math.max(this.min, this.minValue - step);
          if (newValue < this.maxValue) {
            this.minValue = newValue;
            shouldUpdate = true;
          }
        } else {
          newValue = Math.max(this.minValue + step, this.maxValue - step);
          this.maxValue = newValue;
          shouldUpdate = true;
        }
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        if (handle === 'min') {
          newValue = Math.min(this.maxValue - step, this.minValue + step);
          this.minValue = newValue;
          shouldUpdate = true;
        } else {
          newValue = Math.min(this.max, this.maxValue + step);
          this.maxValue = newValue;
          shouldUpdate = true;
        }
        break;
      case 'Home':
        e.preventDefault();
        if (handle === 'min') {
          this.minValue = this.min;
        } else {
          this.maxValue = Math.max(this.minValue + step, this.min + step);
        }
        shouldUpdate = true;
        break;
      case 'End':
        e.preventDefault();
        if (handle === 'min') {
          this.minValue = Math.min(this.maxValue - step, this.max - step);
        } else {
          this.maxValue = this.max;
        }
        shouldUpdate = true;
        break;
    }

    if (shouldUpdate) {
      this.updateSliderPosition();
      this.saveToStorage();
      this.emitRangeUpdate();
    }
  }

  handleResize() {
    requestAnimationFrame(() => {
      this.updateSliderPosition();
    });
  }

  // Public API methods
  setMinValue(value, emitUpdate = true) {
    this.minValue = Math.max(this.min, Math.min(this.maxValue - this.step, value));
    this.updateSliderPosition();
    this.saveToStorage();
    if (emitUpdate) this.emitRangeUpdate();
  }

  setMaxValue(value, emitUpdate = true) {
    this.maxValue = Math.max(this.minValue + this.step, Math.min(this.max, value));
    this.updateSliderPosition();
    this.saveToStorage();
    if (emitUpdate) this.emitRangeUpdate();
  }

  getMinValue() {
    return this.minValue;
  }

  getMaxValue() {
    return this.maxValue;
  }

  updatedCallback() {
    requestAnimationFrame(() => {
      this.updateSlider();
    });
  }

  updateSlider() {
    this.ensureRendered();
    this.syncFromInputsOrAttributes();
    this.updateSliderPosition();
  }

  // Sync with hidden input fields
  syncWithInputs() {
    const container = this.closest('[data-range-holder]');
    if (!container) return;

    this.minInput = container.querySelector('[ref="minInput"]');
    this.maxInput = container.querySelector('[ref="maxInput"]');

    if (this.minInput && this.maxInput) {

      this.updateInputValues();

      this.minInput.addEventListener('change', () => {
        const newValue = parseFloat(this.minInput.value);
        if (!isNaN(newValue) && newValue !== this.minValue) {
          this.setMinValue(newValue);
        }
      });

      this.maxInput.addEventListener('change', () => {
        const newValue = parseFloat(this.maxInput.value);
        if (!isNaN(newValue) && newValue !== this.maxValue) {
          this.setMaxValue(newValue);
        }
      });
    }
  }

  updateInputValues() {
    if (this.minInput) {
      this.minInput.value = this.minValue;
    }
    if (this.maxInput) {
      this.maxInput.value = this.maxValue;
    }
  }

  updateLabels() {
    const container = this.closest('[data-range-holder]');
    if (!container) return;

    const minLabel = container.querySelector('.label-min');
    const maxLabel = container.querySelector('.label-max');

    if (minLabel || maxLabel) {
      const formatPrice = (value) => this.formatMoney(value);

      if (minLabel) {
        minLabel.textContent = formatPrice(this.minValue);
      }
      if (maxLabel) {
        maxLabel.textContent = formatPrice(this.maxValue);
      }
    }
  }

  // Storage methods
  saveToStorage() {
    // Sync state to DOM attributes immediately
    this.setAttribute('min-value', this.minValue);
    this.setAttribute('max-value', this.maxValue);

    if (!this.storageKey) return;

    try {
      const data = {
        minValue: this.minValue,
        maxValue: this.maxValue,
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save price slider state:', e);
    }
  }

  restoreFromStorage() {
    if (!this.storageKey) return false;

    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (!stored) return false;

      const data = JSON.parse(stored);
      if (!data.timestamp || (Date.now() - data.timestamp) > 5 * 60 * 1000) {
        sessionStorage.removeItem(this.storageKey);
        return false;
      }
      if (typeof data.minValue === 'number') {
        this.minValue = data.minValue;
      }
      if (typeof data.maxValue === 'number') {
        this.maxValue = data.maxValue;
      }

      this.validateValues();
      return true;
    } catch (e) {
      console.warn('Failed to restore price slider state:', e);
      return false;
    }
  }

  clearStoredState() {
    if (this.storageKey) {
      sessionStorage.removeItem(this.storageKey);
    }
    this.minValue = this.min;
    this.maxValue = this.max;
    this.updateSliderPosition();
    this.saveToStorage();
  }

  handleFiltersClear() {
    this.clearStoredState();
  }

  ensureRendered() {
    if (this.querySelector('.price-range-slider')) return;
    this.render();
    this.setupEventListeners();
  }

  normalizeStep() {
    if (!Number.isFinite(this.step) || this.step <= 0) {
      this.step = 1;
    }
  }

  syncFromInputsOrAttributes() {
    this.min = parseFloat(this.getAttribute('min')) || 0;
    this.max = parseFloat(this.getAttribute('max')) || 100;
    this.step = parseFloat(this.getAttribute('step')) || 1;
    this.normalizeStep();

    // Prioritize attributes (Server State) over Inputs
    const attrMinValue = parseFloat(this.getAttribute('min-value'));
    const attrMaxValue = parseFloat(this.getAttribute('max-value'));

    if (!isNaN(attrMinValue)) this.minValue = attrMinValue;
    if (!isNaN(attrMaxValue)) this.maxValue = attrMaxValue;

    // Only if attributes are missing, check inputs
    if (isNaN(attrMinValue) && isNaN(attrMaxValue)) {
      const inputValues = this.getInputValues();
      if (inputValues) {
        this.minValue = inputValues.minValue;
        this.maxValue = inputValues.maxValue;
      }
    }

    this.validateValues();
  }

  getInputValues() {
    if (!this.minInput || !this.maxInput) return null;

    const minValue = parseFloat(this.minInput.value);
    const maxValue = parseFloat(this.maxInput.value);

    if (isNaN(minValue) && isNaN(maxValue)) return null;

    return {
      minValue: isNaN(minValue) ? this.min : minValue,
      maxValue: isNaN(maxValue) ? this.max : maxValue,
    };
  }

  emitRangeUpdate() {
    if (!this.debouncedEmit) {
      this.debouncedEmit = this.debounce(() => {
        this.dispatchEvent(new CustomEvent('theme:range:update', { bubbles: true }));
      }, 500);
    }
    this.debouncedEmit();
  }

  debounce(func, wait) {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }

  formatMoney(value) {
    const currency = this.getAttribute('currency-code');
    const locale = document.documentElement.lang || navigator.language || 'en';
    const amount = Number.isFinite(value) ? value : 0;

    if (!currency) return amount.toString();

    const currencyDigits = new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions()
      .maximumFractionDigits;

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: currencyDigits,
    }).format(amount);
  }
}

// Register the custom element
if (!customElements.get('price-range-slider')) {
  customElements.define('price-range-slider', PriceRangeSlider);
}